/* Enhance the cart drawer. Native cart forms and remove links remain fallbacks. */
(() => {
  'use strict';
  if (window.NiralaCartControls) {
    window.NiralaCartControls.init();
    return;
  }

  let busy = false;
  let timer = null;
  let activePromise = null;
  let latestCart = null;
  let uncertainGroup = false;
  let publishingFallback = false;
  const lockedForms = new Map();
  const drawerForm = () => document.querySelector('[data-cart-drawer] [data-cart-controls]');
  const liveForm = form => form?.dataset.cartControlsSurface === 'drawer' || form?.closest('[data-cart-drawer]') ? drawerForm() : [...document.querySelectorAll('[data-cart-controls]')].find(node => !node.closest('[data-cart-drawer]')) || form;
  const enhancedForm = node => {
    const form = node?.closest?.('[data-cart-controls]');
    return form && typeof window.Nirala?.refreshCart === 'function' ? form : null;
  };
  const allInputs = form => [...form.querySelectorAll('[data-cart-quantity]')];
  const inputs = form => allInputs(form).filter(input => !input.hasAttribute('data-cart-child'));
  const dirty = form => inputs(form).some(input => input.value !== input.dataset.cartOriginal);
  const groupOf = item => item?.properties?._nirala_addon_group || '';
  const parentKey = item => item?.parent_relationship?.parent_key || '';
  const isAddon = item => String(item?.properties?._nirala_addon) === 'true';
  const reviewMessage = 'The item and its toppings could not be updated together. Review this order, or remove the main item with its toppings and add it again before checkout.';

  function mismatch(cart) {
    if (!Array.isArray(cart?.items)) return false;
    return cart.items.some(item => {
      const expected = Number(item.properties?._nirala_addon_count || 0);
      if (expected > 0 && !isAddon(item)) {
        const children = cart.items.filter(child => parentKey(child) === item.key && groupOf(child) === groupOf(item) && isAddon(child));
        if (children.length !== expected) return true;
      }
      if (!isAddon(item) || !groupOf(item)) return false;
      const parent = cart.items.find(row => row.key === parentKey(item));
      return !parent || groupOf(parent) !== groupOf(item) || parent.quantity !== item.quantity;
    });
  }

  function checkoutGuard() {
    const blocked = uncertainGroup || mismatch(latestCart) || (!latestCart && Boolean(document.querySelector('[data-cart-addon-mismatch="true"]')));
    document.querySelectorAll('[data-cart-controls]').forEach(form => {
      const button = form.querySelector('[name="checkout"]');
      if (!button) return;
      if (blocked) {
        button.dataset.addonBlocked = 'true';
        window.NiralaBusyControls.set(button, busy, true, false);
        showError(form, reviewMessage);
      } else if (button.dataset.addonBlocked) {
        delete button.dataset.addonBlocked;
        if (!busy && !button.dataset.minimumBlocked && form.dataset.cartControlsEmpty !== 'true') button.disabled = false;
      }
    });
    return blocked;
  }
  const rootURL = () => {
    const root = window.Nirala?.config?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  function showError(form, message) {
    const error = form?.querySelector('[data-cart-error]');
    if (error) { error.textContent = message; error.hidden = !message; }
    else if (message) window.Nirala?.tell?.(message);
  }

  function restoreNote(value, form) {
    if (value === null) return;
    const note = liveForm(form)?.querySelector('[name="note"]');
    if (note) note.value = value;
  }

  function setBusy(form) {
    if (lockedForms.has(form)) return;
    form.setAttribute('aria-busy', 'true');
    const fields = [...form.querySelectorAll('button, input, textarea')];
    const releaseFields = fields.map(field => window.NiralaBusyControls.lock(field, field.name === 'checkout'));
    form.querySelectorAll('[data-cart-remove]').forEach(link => link.setAttribute('aria-disabled', 'true'));
    lockedForms.set(form, () => {
      form.removeAttribute('aria-busy');
      form.querySelectorAll('[data-cart-updating]').forEach(line => line.removeAttribute('data-cart-updating'));
      form.querySelectorAll('[data-cart-removing]').forEach(line => line.removeAttribute('data-cart-removing'));
      releaseFields.forEach(release => release());
      form.querySelectorAll('[data-cart-remove]').forEach(link => link.removeAttribute('aria-disabled'));
    });
  }

  function resetToKnownCart(form, cart) {
    if (!form) return;
    allInputs(form).forEach(input => {
      const item = Array.isArray(cart?.items) ? cart.items.find(row => row.key === input.dataset.cartKey) : null;
      const quantity = Array.isArray(cart?.items) ? item?.quantity || 0 : Number(input.dataset.cartOriginal);
      input.value = String(quantity);
      input.dataset.cartOriginal = String(quantity);
      const display = input.closest('[data-cart-line-key]')?.querySelector('[data-cart-quantity-display]');
      if (display) display.textContent = String(quantity);
      if (cart && !item && Array.isArray(cart.items)) input.closest('[data-cart-line-key]')?.remove();
      if (item && typeof window.Nirala?.formatMoney === 'function') {
        const price = input.closest('[data-cart-line-key]')?.querySelector('[data-cart-line-total]');
        if (price) price.textContent = window.Nirala.formatMoney(item.final_line_price, cart.currency);
      }
    });
    if (cart && typeof window.Nirala?.formatMoney === 'function') {
      const total = form.querySelector('[data-cart-total]');
      if (total) total.textContent = window.Nirala.formatMoney(cart.total_price, cart.currency);
      if (cart.item_count === 0) form.dataset.cartControlsEmpty = 'true';
    }
  }

  function apply(form) {
    clearTimeout(timer);
    timer = null;
    if (busy) return activePromise;
    form.dataset.cartControlsSurface = form.closest('[data-cart-drawer]') ? 'drawer' : 'page';
    const changes = [];
    for (const input of inputs(form)) {
      const quantity = Number(input.value);
      if (input.value.trim() === '' || !Number.isInteger(quantity) || quantity < 0 || quantity > Number(input.max || 99)) {
        showError(form, `Choose a whole-number quantity between 0 and ${input.max || 99}.`);
        input.value = input.dataset.cartOriginal;
        input.focus();
        return Promise.resolve(false);
      }
      if (quantity !== Number(input.dataset.cartOriginal)) changes.push({ id: input.dataset.cartKey, quantity, group: input.dataset.cartAddonParent || '' });
    }
    const noteInput = form.querySelector('[name="note"]');
    const formAction = form.dataset.niralaCartAction || form.getAttribute('action');
    const note = noteInput ? noteInput.value : null;
    const noteChanged = noteInput && noteInput.value !== noteInput.defaultValue;
    if (!changes.length && !noteChanged) { showError(form, ''); return Promise.resolve(true); }
    window.NiralaCartRenderer?.captureFocus?.(form.closest('[data-cart-content]'));
    busy = true;
    changes.forEach(change => {
      allInputs(form).filter(input => input.dataset.cartKey === change.id).forEach(input => input.closest('[data-cart-line-key]')?.setAttribute('data-cart-updating', ''));
    });
    setBusy(form);
    init();
    showError(form, '');

    const runMutation = window.Nirala.mutateCart || (work => work());
    activePromise = runMutation(async () => {
      let knownCart = null;
      let saved = false;
      let failure = '';
      let rollback = null;
      async function readCart() {
        const response = await fetch(rootURL() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error('Your cart could not be verified. Check your connection and try again.');
        const cart = await response.json();
        if (!Array.isArray(cart.items)) throw new Error('Your cart could not be verified. Please reopen your cart.');
        knownCart = cart;
        latestCart = cart;
        return cart;
      }
      async function send(path, body) {
        const response = await fetch(rootURL() + path, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (response.ok) saved = true;
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'This quantity is not available. Please choose another quantity.');
        if (Array.isArray(result.items)) knownCart = result;
        return result;
      }
      // Match the unique add-on group AND the complete line identity, never a variant alone.
      // Shopify can change keys after automatic discounts, so every POST uses the latest exact key.
      const identity = item => JSON.stringify([String(item.variant_id || item.id), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)), item.selling_plan_allocation?.selling_plan?.id || null]);
      function resolveMember(snapshot, group) {
        const parent = knownCart.items.filter(item => groupOf(item) === group && !parentKey(item) && !isAddon(item));
        if (parent.length !== 1) throw new Error(reviewMessage);
        const candidates = knownCart.items.filter(item => groupOf(item) === group && identity(item) === identity(snapshot) && (isAddon(snapshot) ? parentKey(item) === parent[0].key : item.key === parent[0].key));
        if (candidates.length !== 1) throw new Error(reviewMessage);
        return candidates[0];
      }
      async function changeMember(snapshot, quantity, group) {
        const member = resolveMember(snapshot, group);
        if (member.quantity === quantity) return;
        if (member.instructions?.can_update_quantity === false) throw new Error('This item cannot change quantity. Remove it and add the required quantity again.');
        await send('cart/change.js', { id: member.key, quantity });
        if (!knownCart || resolveMember(snapshot, group).quantity !== quantity) throw new Error('The requested quantity is not available for every topping.');
      }
      try {
        if (changes.length) await readCart();
        for (const change of changes) {
          if (change.group) {
            await readCart();
            const parent = knownCart.items.find(item => item.key === change.id && groupOf(item) === change.group && !parentKey(item));
            if (!parent) throw new Error('Your cart changed while updating. Review the quantities below before making another change.');
            const children = knownCart.items.filter(item => parentKey(item) === parent.key && groupOf(item) === change.group && isAddon(item));
            if (change.quantity === 0) {
              if (parent.instructions?.can_remove === false) throw new Error('This item cannot be removed from the cart.');
              // Shopify owns cascading deletion. Never issue a second removal for a child.
              await send('cart/change.js', { id: parent.key, quantity: 0 });
              continue;
            }
            if (!children.length) throw new Error(reviewMessage);
            rollback = { group: change.group, members: [...children, parent] };
            for (const member of rollback.members) await changeMember(member, change.quantity, change.group);
            rollback = null;
            continue;
          }
          if (knownCart && !knownCart.items.some(item => item.key === change.id)) {
            throw new Error('Your cart changed while updating. Review the quantities below before making another change.');
          }
          await send('cart/change.js', { id: change.id, quantity: change.quantity });
        }
        if (noteChanged) await send('cart/update.js', { note });
      } catch (error) {
        failure = error.message || 'We could not confirm your changes. Please check your cart before trying again.';
        if (rollback) {
          // A failed request may still have changed stock. Re-read before best-effort recovery.
          try {
            await readCart();
            for (const member of rollback.members) await changeMember(member, member.quantity, rollback.group);
          } catch (_) { uncertainGroup = true; }
        }
      }

      let refreshed = false;
      try {
        if (saved && !failure && Array.isArray(knownCart?.items)) window.Nirala.publishCart(knownCart);
        else knownCart = await window.Nirala.refreshCart({ fresh: true });
        latestCart = knownCart;
        uncertainGroup = false;
        refreshed = true;
      } catch (_) {
        if (!failure) failure = saved
          ? 'Your changes were saved, but the cart display could not refresh. Close and reopen your cart to reload the order.'
          : 'The cart could not refresh. Please check your connection and reopen your cart.';
      }

      if (knownCart && !refreshed) {
        publishingFallback = true;
        window.Nirala.publishCart(knownCart);
        publishingFallback = false;
      } else if (!refreshed) resetToKnownCart(liveForm(form) || form, knownCart);
      // A rejected change can return the same cart fingerprint, preserving its
      // form. Replace the attempted quantity with the confirmed server value.
      if (knownCart) document.querySelectorAll('[data-cart-controls]').forEach(current => resetToKnownCart(current, knownCart));
      restoreNote(note, form);
      if (formAction && liveForm(form)) liveForm(form).setAttribute('action', formAction);
      if (failure) showError(liveForm(form) || form, failure);
      checkoutGuard();
      return !failure && !uncertainGroup && !mismatch(knownCart);
    }).finally(() => {
      lockedForms.forEach(release => release());
      lockedForms.clear();
      busy = false;
      activePromise = null;
      init();
    });
    return activePromise;
  }

  function init() {
    document.querySelectorAll('[data-cart-controls]').forEach(form => {
      if (!enhancedForm(form)) return;
      form.dataset.cartControlsSurface = form.closest('[data-cart-drawer]') ? 'drawer' : 'page';
      form.querySelectorAll('[data-cart-step]').forEach(button => { button.hidden = false; });
      form.querySelectorAll('[data-cart-addon-parent]').forEach(input => { input.readOnly = false; });
      if (busy) setBusy(form);
      if (form.dataset.cartControlsEmpty === 'true') form.querySelector('[name="checkout"]')?.setAttribute('disabled', 'disabled');
    });
    checkoutGuard();
    window.Nirala?.syncMinimumOrder(latestCart);
    if (!busy) window.NiralaCartRenderer?.restoreFocus?.();
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-cart-step]');
    const remove = event.target.closest('[data-cart-remove]');
    const form = enhancedForm(button || remove);
    if (!form) return;
    if (remove && (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || event.button !== 0)) return;
    event.preventDefault();
    if (busy) return;
    const input = (button || remove).closest('[data-cart-line-key]')?.querySelector('[data-cart-quantity]');
    if (!input) return;
    if (remove) remove.closest('[data-cart-line-key]')?.setAttribute('data-cart-removing', '');
    const current = Number(input.value);
    input.value = String(remove ? 0 : Math.max(0, Math.min(Number(input.max || 99), (Number.isFinite(current) ? current : Number(input.dataset.cartOriginal)) + Number(button.dataset.cartStep))));
    apply(form);
  });

  document.addEventListener('input', event => {
    if (!event.target.matches('[data-cart-quantity]')) return;
    const form = enhancedForm(event.target);
    if (!form || busy) return;
    clearTimeout(timer);
    timer = setTimeout(() => { timer = null; apply(form); }, 400);
  });

  document.addEventListener('submit', event => {
    const form = enhancedForm(event.target);
    if (!form || form.dataset.readySubmit) return;
    const checkout = event.submitter?.name === 'checkout';
    if (checkout && checkoutGuard()) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (checkout && !busy && !dirty(form) && !timer) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const promise = busy ? activePromise : apply(form);
    if (checkout) Promise.resolve(promise).then(success => {
      if (!success) return;
      const current = liveForm(form);
      const button = current?.querySelector('[name="checkout"]');
      if (button && !button.disabled) current.requestSubmit(button);
    });
  }, true);

  document.addEventListener('nirala:cart-updated', event => {
    if (Array.isArray(event.detail?.items)) {
      latestCart = event.detail;
      if (!publishingFallback) uncertainGroup = false;
    }
    init();
  });
  document.addEventListener('shopify:section:load', init);
  window.NiralaCartControls = { init, apply, checkoutGuard, isBusy: () => busy || timer !== null };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
